import { useEffect, useRef, useState } from 'react'

import { InlineNotice } from '../../components/ui'
import { uploadLimits, validateDetails, validateFile, validateStrains } from '../../lib/validation'
import { submissionRepository } from '../../services/submissions'
import {
  createEmptyDraft,
  createEmptyStrain,
  newId,
  RELEASE_VERSION,
  type SubmissionDraft,
  type UploadAsset,
} from '../../types/submission'
import { DetailsStep } from './DetailsStep'
import { ReviewStep } from './ReviewStep'
import { StrainsStep } from './StrainsStep'
import { SubmissionShell } from './SubmissionShell'
import { SubmissionSuccess } from './SubmissionSuccess'

interface NoticeState {
  tone: 'info' | 'success' | 'error'
  message: string
}

export function SubmissionPage() {
  const [draft, setDraft] = useState<SubmissionDraft>(createEmptyDraft)
  const [maxStep, setMaxStep] = useState<1 | 2 | 3>(1)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [completedId, setCompletedId] = useState<string | null>(null)
  const [restoring, setRestoring] = useState(true)
  const draftRef = useRef(draft)
  const identityPromiseRef = useRef<Promise<{ id: string; ownerUid: string }> | null>(null)
  const objectUrlsRef = useRef(new Set<string>())

  useEffect(() => {
    const objectUrls = objectUrlsRef.current
    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  useEffect(() => {
    let active = true
    submissionRepository.loadDraft()
      .then((restored) => {
        if (!active || !restored) return
        const currentTermsDraft = restored.releaseVersion === RELEASE_VERSION
          ? restored
          : { ...restored, consentAccepted: false, releaseVersion: RELEASE_VERSION }
        draftRef.current = currentTermsDraft
        setDraft(currentTermsDraft)
        setMaxStep(restored.step)
        for (const strain of restored.strains) {
          for (const asset of strain.assets) {
            if (asset.previewUrl.startsWith('blob:')) objectUrlsRef.current.add(asset.previewUrl)
          }
        }
        setNotice({ tone: 'info', message: 'Your saved draft has been restored.' })
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setRestoring(false)
      })
    return () => { active = false }
  }, [])

  const updateDraft = (updater: SubmissionDraft | ((current: SubmissionDraft) => SubmissionDraft)) => {
    setDraft((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater
      draftRef.current = next
      return next
    })
  }

  const persist = async (target = draftRef.current) => {
    let identity: { id: string; ownerUid: string }
    if (target.id && target.ownerUid) {
      identity = await submissionRepository.saveDraft(target)
    } else {
      identityPromiseRef.current ??= submissionRepository.saveDraft(target)
      try {
        identity = await identityPromiseRef.current
      } catch (error) {
        identityPromiseRef.current = null
        throw error
      }
    }
    updateDraft((current) => ({ ...current, id: identity.id, ownerUid: identity.ownerUid }))
    return identity
  }

  const updateAsset = (strainId: string, assetId: string, patch: Partial<UploadAsset>) => {
    updateDraft((current) => ({
      ...current,
      strains: current.strains.map((strain) =>
        strain.id === strainId
          ? {
              ...strain,
              assets: strain.assets.map((asset) =>
                asset.id === assetId ? { ...asset, ...patch } : asset,
              ),
            }
          : strain,
      ),
    }))
  }

  const handleDetailsContinue = async () => {
    const validation = validateDetails(draft.details)
    if (!validation.valid) {
      setNotice({ tone: 'error', message: validation.message ?? 'Check your details.' })
      return
    }
    setSaving(true)
    setNotice(null)
    try {
      await persist()
      updateDraft((current) => ({ ...current, step: 2 }))
      setMaxStep((current) => (current < 2 ? 2 : current))
    } catch (error) {
      setNotice({ tone: 'error', message: getErrorMessage(error) })
    } finally {
      setSaving(false)
    }
  }

  const handleFiles = async (strainId: string, files: File[]) => {
    const strain = draftRef.current.strains.find((item) => item.id === strainId)
    if (!strain) return
    const availableSlots = uploadLimits.maxImagesPerStrain - strain.assets.length
    const acceptedFiles = files.slice(0, availableSlots)

    if (files.length > availableSlots) {
      setNotice({
        tone: 'error',
        message: `Each strain can include up to ${uploadLimits.maxImagesPerStrain} images.`,
      })
    }

    const assets: UploadAsset[] = []
    for (const file of acceptedFiles) {
      const validation = validateFile(file)
      if (!validation.valid) {
        setNotice({ tone: 'error', message: validation.message ?? 'Choose a supported image.' })
        continue
      }
      const previewUrl = URL.createObjectURL(file)
      objectUrlsRef.current.add(previewUrl)
      assets.push({
        id: newId(),
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl,
        progress: 0,
        status: 'queued',
        file,
      })
    }
    if (!assets.length) return

    const nextDraft: SubmissionDraft = {
      ...draftRef.current,
      strains: draftRef.current.strains.map((item) =>
        item.id === strainId ? { ...item, assets: [...item.assets, ...assets] } : item,
      ),
    }
    updateDraft(nextDraft)

    try {
      const identity = await persist(nextDraft)
      await Promise.all(
        assets.map(async (asset) => {
          updateAsset(strainId, asset.id, { status: 'uploading' })
          try {
            const result = await submissionRepository.uploadAsset({
              submissionId: identity.id,
              strainId,
              asset,
              onProgress: (progress) =>
                updateAsset(strainId, asset.id, {
                  progress,
                  status: progress === 100 ? 'processing' : 'uploading',
                }),
            })
            updateAsset(strainId, asset.id, { ...result, progress: 100 })
          } catch (error) {
            updateAsset(strainId, asset.id, {
              status: 'error',
              error: getErrorMessage(error),
            })
          }
        }),
      )
      setNotice({ tone: 'success', message: `${assets.length} ${assets.length === 1 ? 'image is' : 'images are'} ready.` })
    } catch (error) {
      setNotice({ tone: 'error', message: getErrorMessage(error) })
    }
  }

  const handleRemoveAsset = async (strainId: string, asset: UploadAsset) => {
    updateDraft((current) => ({
      ...current,
      strains: current.strains.map((strain) =>
        strain.id === strainId
          ? { ...strain, assets: strain.assets.filter((item) => item.id !== asset.id) }
          : strain,
      ),
    }))
    if (objectUrlsRef.current.delete(asset.previewUrl)) URL.revokeObjectURL(asset.previewUrl)
    if (draftRef.current.id) {
      try {
        await submissionRepository.removeAsset(draftRef.current.id, strainId, asset)
      } catch (error) {
        setNotice({ tone: 'error', message: getErrorMessage(error) })
      }
    }
  }

  const handleRemoveStrain = async (strainId: string) => {
    const strain = draftRef.current.strains.find((item) => item.id === strainId)
    if (!strain || draftRef.current.strains.length === 1) return
    updateDraft((current) => ({
      ...current,
      strains: current.strains.filter((item) => item.id !== strainId),
    }))
    await Promise.all(strain.assets.map((asset) => handleRemoveAsset(strainId, asset)))
    if (draftRef.current.id) await submissionRepository.removeStrain(draftRef.current.id, strainId)
  }

  const handleSave = async () => {
    setSaving(true)
    setNotice(null)
    try {
      await persist()
      setNotice({ tone: 'success', message: 'Draft saved.' })
    } catch (error) {
      setNotice({ tone: 'error', message: getErrorMessage(error) })
    } finally {
      setSaving(false)
    }
  }

  const handleReview = async () => {
    const validation = validateStrains(draft.strains)
    if (!validation.valid) {
      setNotice({ tone: 'error', message: validation.message ?? 'Check your strains and images.' })
      return
    }
    setSaving(true)
    try {
      await persist()
      updateDraft((current) => ({ ...current, step: 3 }))
      setMaxStep(3)
      setNotice(null)
    } catch (error) {
      setNotice({ tone: 'error', message: getErrorMessage(error) })
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!draft.consentAccepted) {
      setNotice({ tone: 'error', message: 'Confirm the contributor terms before submitting.' })
      return
    }
    setSubmitting(true)
    setNotice(null)
    try {
      const identity = await persist()
      const result = await submissionRepository.finalize(identity.id)
      setCompletedId(result.submissionId)
    } catch (error) {
      setNotice({ tone: 'error', message: getErrorMessage(error) })
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current.clear()
    identityPromiseRef.current = null
    const empty = createEmptyDraft()
    draftRef.current = empty
    setDraft(empty)
    setMaxStep(1)
    setNotice(null)
    setCompletedId(null)
  }

  if (completedId) return <SubmissionSuccess onStartAnother={reset} />

  if (restoring) return <div className="route-loading route-loading--light">Restoring your draft…</div>

  return (
    <SubmissionShell
      step={draft.step}
      maxStep={maxStep}
      onStepChange={(step) => updateDraft((current) => ({ ...current, step }))}
    >
      {notice ? <InlineNotice tone={notice.tone}>{notice.message}</InlineNotice> : null}

      {draft.step === 1 ? (
        <DetailsStep
          details={draft.details}
          onChange={(details) => updateDraft((current) => ({ ...current, details }))}
          onContinue={handleDetailsContinue}
          saving={saving}
        />
      ) : null}

      {draft.step === 2 ? (
        <StrainsStep
          strains={draft.strains}
          onChange={(strains) => updateDraft((current) => ({ ...current, strains }))}
          onFiles={handleFiles}
          onRemoveAsset={handleRemoveAsset}
          onAddStrain={() => {
            if (draft.strains.length >= uploadLimits.maxStrains) {
              setNotice({ tone: 'error', message: `A submission can include up to ${uploadLimits.maxStrains} strains.` })
              return
            }
            updateDraft((current) => ({ ...current, strains: [...current.strains, createEmptyStrain()] }))
          }}
          onRemoveStrain={handleRemoveStrain}
          onSave={handleSave}
          onReview={handleReview}
          saving={saving}
        />
      ) : null}

      {draft.step === 3 ? (
        <ReviewStep
          draft={draft}
          onConsentChange={(consentAccepted) => updateDraft((current) => ({ ...current, consentAccepted }))}
          onEdit={(step) => updateDraft((current) => ({ ...current, step }))}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      ) : null}
    </SubmissionShell>
  )
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Something went wrong. Please try again.'
}

export default SubmissionPage
